import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Overview from './Overview';
import MyDashboard from './MyDashboard';
import LiveCalls from './LiveCalls';
import IncomingCalls from './IncomingCalls';
import IvrPressStats from './IvrPressStats';
import AllFieldsTable from './AllFieldsTable';
import AgentDetails from './AgentDetails';
import TrendAnalytics from './TrendAnalytics';
import QueryPanel from './QueryPanel';
import FollowUpPanel from './FollowUpPanel';
import DailyOrders from './DailyOrders';
import RtoStockedIn from './RtoStockedIn';
import OrderQueries from './OrderQueries';
import AbandonedCarts from './AbandonedCarts';
import WhatsAppOverview from './WhatsAppOverview';
import EmailOverview from './EmailOverview';
import IntlOrders from './IntlOrders';
import PaymentLinks from './PaymentLinks';
import BackendPayments from './BackendPayments';
import DamagedProducts from './DamagedProducts';
import Refunds from './Refunds';
import Ndr from './Ndr';
import ManageAdmins from './ManageAdmins';
import DirectCall from './DirectCall';
import Login from './Login';
import ThemeToggle from './ThemeToggle';
import ChangePasswordModal from './ChangePasswordModal';
import { useAuth, isRefreshFor } from './AuthContext';
import { mergeCalls } from './callsMerge';
import logo from './logo.png';
import {
  PhoneIcon,
  UsersIcon,
  TableIcon,
  TrashIcon,
  MenuIcon,
  CloseIcon,
  ShieldIcon,
  LogoutIcon,
  DialpadIcon,
  MessageIcon,
  KeyIcon,
  TrendIcon,
  CalendarClockIcon,
  CalendarIcon,
  CartIcon,
  ChevronsLeftIcon,
  LayoutDashboardIcon,
  RefreshIcon,
  FlagIcon,
  HashIcon,
  WhatsAppIcon,
  GlobeIcon,
  MailIcon,
  LinkIcon,
  WalletIcon,
  RefundIcon,
  TruckIcon,
  LayoutGridIcon,
  PackageReturnIcon,
  DamagedPackageIcon,
} from './Icons';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', subtitle: 'A single-screen summary of every section', icon: LayoutGridIcon },
  { key: 'livecalls', label: 'Live Calls', subtitle: 'Calls ringing on the server right now', icon: RefreshIcon },
  { key: 'mydashboard', label: 'My Dashboard', subtitle: 'Your own call stats & performance', icon: LayoutDashboardIcon },
  { key: 'incoming', label: 'Incoming Calls', subtitle: 'Real-time IVR call activity', icon: PhoneIcon },
  { key: 'ivrpresses', label: 'IVR Press Stats', subtitle: 'How many calls pressed each IVR menu option', icon: HashIcon },
  { key: 'dialpad', label: 'Direct Call', subtitle: 'Dial any number on demand', icon: DialpadIcon },
  { key: 'agents', label: 'Agents', subtitle: 'Performance by agent', icon: UsersIcon },
  { key: 'trends', label: 'Trends', subtitle: 'Call volume & answer rate over time', icon: TrendIcon },
  { key: 'query', label: 'Query', subtitle: 'Saved customer queries', icon: MessageIcon },
  { key: 'followup', label: 'Follow Up', subtitle: 'Customers due for a follow-up', icon: CalendarClockIcon },
  { key: 'dailyorders', label: 'Daily Orders', subtitle: 'ERP orders still awaiting a COD confirmation call', icon: CalendarIcon },
  { key: 'rtostockedin', label: 'RTO Stocked In', subtitle: 'Returned orders back in stock with money still owed', icon: PackageReturnIcon },
  { key: 'orderqueries', label: 'Order Queries', subtitle: 'Raise or look up ERP order queries', icon: FlagIcon },
  { key: 'abandonedcarts', label: 'Abandoned Carts', subtitle: 'Shopify checkouts started but never completed', icon: CartIcon },
  { key: 'whatsapp', label: 'WhatsApp', subtitle: 'Every WhatsApp conversation, in one place', icon: WhatsAppIcon },
  { key: 'email', label: 'Email', subtitle: 'Every support email, in one place', icon: MailIcon },
  { key: 'international', label: 'International', subtitle: 'International orders: logistics, payment and shipment', icon: GlobeIcon },
  { key: 'paymentlinks', label: 'Payment Links', subtitle: 'Create and track Razorpay payment links', icon: LinkIcon },
  { key: 'backendpayments', label: 'Backend Payments', subtitle: 'Log manual payouts made to customers', icon: WalletIcon },
  { key: 'damagedproducts', label: 'Damaged Products', subtitle: 'Products customers received damaged, with photos', icon: DamagedPackageIcon },
  { key: 'refunds', label: 'Refunds', subtitle: 'Razorpay refunds and their status', icon: RefundIcon },
  { key: 'ndr', label: 'NDR', subtitle: 'Undelivered shipments from Jiffy and ParcelX, live', icon: TruckIcon },
  { key: 'all', label: 'All Fields', subtitle: 'Raw webhook payload data', icon: TableIcon },
];

const SUPERADMIN_NAV_ITEM = {
  key: 'admins',  
  label: 'Manage Admins',
  subtitle: 'Create and remove admin accounts',
  icon: ShieldIcon,
};

function App() {
  const { user, logout, authFetch, checkingSession, subscribeRefresh, subscribeCalls } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [queryAgentFilter, setQueryAgentFilter] = useState('');
  // WhatsApp number handed from a chat's "International order" button to the
  // International page's create form.
  const [intlOrderPrefill, setIntlOrderPrefill] = useState(null);
  const clearIntlOrderPrefill = useCallback(() => setIntlOrderPrefill(null), []);
  // A customer number handed from the Email page's customer panel to the
  // WhatsApp page, which opens that chat.
  const [whatsappOpenNumber, setWhatsappOpenNumber] = useState(null);
  const clearWhatsappOpenNumber = useCallback(() => setWhatsappOpenNumber(null), []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Drill-down from Agent Details: jump to the Query page pre-filtered to
  // whatever that agent's calls saved there (name/number already double as
  // the Query page's search term).
  const handleViewAgentQueries = (agentName) => {
    setQueryAgentFilter(agentName);
    setActiveTab('query');
  };

  const isSuperadmin = user?.role === 'superadmin';
  // The International tab also shows for roles that only have its logistics,
  // packing, cancel or admin capability (see ALL_PAGE_PERMISSIONS in server/db.js).
  const userPermissions = new Set(user?.permissions || []);
  if (['international_logistics', 'international_packing', 'international_cancel', 'international_admin'].some((p) => userPermissions.has(p))) {
    userPermissions.add('international');
  }
  const allowedItems = isSuperadmin
    ? NAV_ITEMS
    : NAV_ITEMS.filter((item) => userPermissions.has(item.key));
  const canOpenIntlOrders = userPermissions.has('international') || isSuperadmin;
  const navItems = isSuperadmin ? [...allowedItems, SUPERADMIN_NAV_ITEM] : allowedItems;

  // If the current tab isn't (or is no longer) permitted — e.g. right after
  // login, or a role's permissions changed — fall back to the first tab this
  // user can actually see.
  useEffect(() => {
    if (!user) return;
    if (navItems.some((item) => item.key === activeTab)) return;
    if (navItems.length > 0) setActiveTab(navItems[0].key);
  }, [user, navItems, activeTab]);

  // Keeps `calls` in sync without re-downloading the whole call history on
  // every update: one full load of the list view, then only the calls
  // changed since the last load's cursor (X-Calls-Cursor), merged in by id.
  // See server/callSync.js. One request at a time; anything asked for while
  // one is running is folded into a single follow-up ('full' wins over
  // 'delta'). If the server sends no cursor, every load is a full one, which
  // is exactly the old behaviour.
  const callsSyncRef = useRef({ cursor: null, inFlight: false, queued: null });
  const loadCalls = useCallback(
    async (mode = 'delta') => {
      const sync = callsSyncRef.current;
      if (sync.inFlight) {
        if (sync.queued !== 'full') sync.queued = mode;
        return;
      }
      sync.inFlight = true;
      try {
        const full = mode === 'full' || sync.cursor === null;
        const url = full ? '/api/ivr-calls?view=list' : `/api/ivr-calls?view=list&since=${sync.cursor}`;
        const response = await authFetch(url);
        const data = await response.json();
        if (!response.ok || !Array.isArray(data)) {
          throw new Error(data?.error || `HTTP ${response.status}`);
        }
        const cursorHeader = response.headers.get('X-Calls-Cursor');
        const cursor = cursorHeader === null ? NaN : Number(cursorHeader);
        const hasCursor = Number.isSafeInteger(cursor);
        if (full) {
          setCalls(data);
          sync.cursor = hasCursor ? cursor : null;
        } else if (!hasCursor || cursor < sync.cursor) {
          // Calls were reset (or the cursor is unusable): start over.
          sync.cursor = null;
          sync.queued = 'full';
        } else {
          if (data.length > 0) setCalls((previous) => mergeCalls(previous, data));
          sync.cursor = cursor;
        }
        setLoading(false);
      } catch (error) {
        // Keeps showing the last calls that loaded.
        console.error('Error fetching calls:', error);
        setLoading(false);
      } finally {
        sync.inFlight = false;
        const next = sync.queued;
        sync.queued = null;
        if (next) loadCalls(next);
      }
    },
    [authFetch]
  );
  // Passed to LiveCalls as refreshCalls (its fast poll), so a plain call
  // always means "catch up on changes".
  const fetchCalls = useCallback(() => loadCalls('delta'), [loadCalls]);

  useEffect(() => {
    if (!user) return;
    loadCalls('full');
    // Only call-list changes refetch calls. The generic "refresh" event
    // (WhatsApp, email, viewing badges, …) no longer reloads them; every
    // server write to the calls table sends a "calls" event.
    const unsubscribe = subscribeCalls((event) => loadCalls(event?.action === 'reset' ? 'full' : 'delta'));
    // Long-interval safety net in case the live-update connection (SSE) is
    // ever blocked (e.g. a proxy that buffers streaming responses).
    const interval = setInterval(() => loadCalls('delta'), 60000);
    // And an occasional full reload, so nothing can drift for long.
    const fullInterval = setInterval(() => loadCalls('full'), 10 * 60000);
    return () => {
      unsubscribe();
      clearInterval(interval);
      clearInterval(fullInterval);
    };
  }, [user, subscribeCalls, loadCalls]);

  // Unread-chats badge on the sidebar's WhatsApp item. Only fetched for
  // users who can see that page. Refreshed on the same live "refresh"
  // events as everything else (a new message fires one), plus a slow
  // safety-net poll. One request at a time, so a burst of events doesn't
  // stack up requests.
  const canSeeWhatsapp = navItems.some((item) => item.key === 'whatsapp');
  const [whatsappUnread, setWhatsappUnread] = useState(0);
  const unreadInFlightRef = useRef(false);
  const fetchWhatsappUnread = useCallback(async () => {
    if (unreadInFlightRef.current) return;
    unreadInFlightRef.current = true;
    try {
      const response = await authFetch('/api/whatsapp/unread-count');
      const data = await response.json();
      if (response.ok) setWhatsappUnread(Number(data.chats) || 0);
    } catch (error) {
      // Badge just keeps its last value.
    } finally {
      unreadInFlightRef.current = false;
    }
  }, [authFetch]);

  useEffect(() => {
    if (!user || !canSeeWhatsapp) return undefined;
    fetchWhatsappUnread();
    // Ticks, notes and Open/Pending/Resolved changes can't change which
    // chats have unread customer messages, so those events skip the recount.
    const NOT_UNREAD_EVENTS = ['message_status', 'note_created', 'note_deleted', 'conversation_state'];
    const unsubscribe = subscribeRefresh((event) => {
      if (isRefreshFor(event, 'whatsapp') && !NOT_UNREAD_EVENTS.includes(event?.type)) fetchWhatsappUnread();
    });
    const interval = setInterval(fetchWhatsappUnread, 30000);
    // Fired by WhatsAppChat.js when it marks a chat read.
    window.addEventListener('whatsapp-read', fetchWhatsappUnread);
    return () => {
      unsubscribe();
      clearInterval(interval);
      window.removeEventListener('whatsapp-read', fetchWhatsappUnread);
    };
  }, [user, canSeeWhatsapp, subscribeRefresh, fetchWhatsappUnread]);

  // Same badge for the Email item: conversations with an unread customer
  // email (GET /api/email/summary, one pass over the email thread summary
  // table). Recounted on email live events that can change it, when
  // EmailThread.js marks a conversation read or unread, and every 60s.
  const canSeeEmail = navItems.some((item) => item.key === 'email');
  const [emailUnread, setEmailUnread] = useState(0);
  const emailUnreadInFlightRef = useRef(false);
  const fetchEmailUnread = useCallback(async () => {
    if (emailUnreadInFlightRef.current) return;
    emailUnreadInFlightRef.current = true;
    try {
      const response = await authFetch('/api/email/summary');
      const data = await response.json();
      if (response.ok) setEmailUnread(Number(data.unread) || 0);
    } catch (error) {
      // Badge just keeps its last value.
    } finally {
      emailUnreadInFlightRef.current = false;
    }
  }, [authFetch]);

  useEffect(() => {
    if (!user || !canSeeEmail) return undefined;
    fetchEmailUnread();
    const NOT_UNREAD_EVENTS = ['conversation_state', 'message_updated'];
    let timer = null;
    const unsubscribe = subscribeRefresh((event) => {
      if (!isRefreshFor(event, 'email') || NOT_UNREAD_EVENTS.includes(event?.type)) return;
      clearTimeout(timer);
      timer = setTimeout(fetchEmailUnread, 300);
    });
    const interval = setInterval(fetchEmailUnread, 60000);
    window.addEventListener('email-read', fetchEmailUnread);
    return () => {
      unsubscribe();
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('email-read', fetchEmailUnread);
    };
  }, [user, canSeeEmail, subscribeRefresh, fetchEmailUnread]);

  const navBadges = { whatsapp: whatsappUnread, email: emailUnread };

  const handleReset = async () => {
    const confirmed = window.confirm(
      'This will permanently delete all stored call data so you can test the webhook fresh. Continue?'
    );
    if (!confirmed) return;

    try {
      const response = await authFetch('/api/ivr-calls', { method: 'DELETE' });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        window.alert(body.error || 'Reset Data failed.');
        return;
      }
      await loadCalls('full');
    } catch (error) {
      console.error('Error resetting calls:', error);
    }
  };

  if (checkingSession) {
    return <div className="loading-state">Loading…</div>;
  }

  if (!user) {
    return <Login />;
  }

  if (navItems.length === 0) {
    return (
      <div className="loading-state">
        No pages have been assigned to your account yet. Ask a superadmin to assign you a role.
        <button className="btn btn-secondary" onClick={logout} style={{ marginTop: '1rem' }}>
          Log out
        </button>
      </div>
    );
  }

  const activeItem = activeTab === 'admins' ? SUPERADMIN_NAV_ITEM : NAV_ITEMS.find((item) => item.key === activeTab);

  const handleNavSelect = (key) => {
    // A direct sidebar click always means "show everything" — only the
    // Agent Details drill-down (handleViewAgentQueries, which bypasses this
    // function) should pre-filter the Query page.
    setQueryAgentFilter('');
    setActiveTab(key);
    setNavOpen(false);
  };

  return (
    <div className={`App ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {navOpen && <div className="sidebar-overlay" onClick={() => setNavOpen(false)} />}

      <aside className={`sidebar ${navOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <button
          className="sidebar-collapse-toggle"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronsLeftIcon className="sidebar-collapse-icon" width={14} height={14} />
        </button>

        <div className="sidebar-brand">
          <img src={logo} alt="Rajarani Product" className="sidebar-logo" />
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-name">Rajarani Product</span>
            <span className="sidebar-brand-tag">IVR Dashboard</span>
          </div>
          <button className="sidebar-close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const badge = navBadges[item.key] || 0;
            return (
              <button
                key={item.key}
                className={`sidebar-nav-item ${activeTab === item.key ? 'active' : ''}`}
                onClick={() => handleNavSelect(item.key)}
                title={
                  badge > 0
                    ? `${item.label} — ${badge} unread ${item.key === 'email' ? 'conversation' : 'chat'}${badge === 1 ? '' : 's'}`
                    : item.label
                }
              >
                <Icon className="sidebar-nav-icon" />
                <span>{item.label}</span>
                {badge > 0 && (
                  <strong className="sidebar-nav-badge" aria-label={`${badge} unread`}>
                    {badge > 999 ? '999+' : badge}
                  </strong>
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-user-avatar" title={user.username}>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="sidebar-user-avatar-img" />
            ) : (
              user.username.slice(0, 1).toUpperCase()
            )}
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user.username}</span>
            <span className="sidebar-user-role">{user.role}</span>
          </div>
          <div className="sidebar-user-actions">
            <button
              className="sidebar-logout"
              onClick={() => setShowChangePassword(true)}
              aria-label="Change password"
              title="Change password"
            >
              <KeyIcon width={16} height={16} />
            </button>
            <button className="sidebar-logout" onClick={logout} aria-label="Log out" title="Log out">
              <LogoutIcon width={16} height={16} />
            </button>
          </div>
        </div>

        <div className="sidebar-footer">
          <span className="sidebar-status-dot" />
          <span>Live · real-time updates</span>
        </div>
      </aside>

      <div className={`App-content ${activeTab === 'whatsapp' || activeTab === 'email' ? 'App-content-full' : ''}`}>
        <header className={`App-header ${activeTab === 'whatsapp' ? 'App-header-compact' : ''}`}>
          <div className="App-header-left">
            <button className="menu-toggle" onClick={() => setNavOpen(true)} aria-label="Open menu">
              <MenuIcon />
            </button>
            <div>
              <h1>{activeItem.label}</h1>
              <p>{activeItem.subtitle}</p>
            </div>
          </div>
          <div className="App-header-right">
            <ThemeToggle />
            {activeTab !== 'admins' && user?.role === 'superadmin' && (
              <button className="btn btn-danger-outline" onClick={handleReset}>
                <TrashIcon width={16} height={16} />
                Reset Data
              </button>
            )}
          </div>
        </header>

        <main
          className={`App-main ${activeTab === 'whatsapp' || activeTab === 'email' ? 'App-main-full' : ''} ${
            activeTab === 'email' ? 'App-main-email' : ''
          }`}
        >
          {activeTab === 'admins' ? (
            <ManageAdmins calls={calls} />
          ) : loading ? (
            <div className="loading-state">Loading…</div>
          ) : activeTab === 'overview' ? (
            <Overview calls={calls} isSuperadmin={isSuperadmin} navItems={navItems} onNavigate={handleNavSelect} />
          ) : activeTab === 'mydashboard' ? (
            <MyDashboard calls={calls} />
          ) : activeTab === 'livecalls' ? (
            <LiveCalls calls={calls} refreshCalls={fetchCalls} />
          ) : activeTab === 'incoming' ? (
            <IncomingCalls calls={calls} />
          ) : activeTab === 'ivrpresses' ? (
            <IvrPressStats calls={calls} />
          ) : activeTab === 'dialpad' ? (
            <DirectCall calls={calls} />
          ) : activeTab === 'agents' ? (
            <AgentDetails calls={calls} onSelectAgent={handleViewAgentQueries} />
          ) : activeTab === 'trends' ? (
            <TrendAnalytics calls={calls} />
          ) : activeTab === 'query' ? (
            <QueryPanel calls={calls} initialSearch={queryAgentFilter} />
          ) : activeTab === 'followup' ? (
            <FollowUpPanel calls={calls} />
          ) : activeTab === 'dailyorders' ? (
            <DailyOrders calls={calls} />
          ) : activeTab === 'rtostockedin' ? (
            <RtoStockedIn calls={calls} />
          ) : activeTab === 'orderqueries' ? (
            <OrderQueries />
          ) : activeTab === 'abandonedcarts' ? (
            <AbandonedCarts calls={calls} />
          ) : activeTab === 'whatsapp' ? (
            <WhatsAppOverview
              calls={calls}
              openNumber={whatsappOpenNumber}
              onOpenNumberConsumed={clearWhatsappOpenNumber}
              onCreateInternationalOrder={
                canOpenIntlOrders
                  ? (number) => {
                      setIntlOrderPrefill(number);
                      handleNavSelect('international');
                    }
                  : undefined
              }
            />
          ) : activeTab === 'email' ? (
            <EmailOverview
              calls={calls}
              onOpenWhatsapp={
                canSeeWhatsapp
                  ? (number) => {
                      setWhatsappOpenNumber(number);
                      handleNavSelect('whatsapp');
                    }
                  : undefined
              }
            />
          ) : activeTab === 'international' ? (
            <IntlOrders prefillNumber={intlOrderPrefill} onPrefillConsumed={clearIntlOrderPrefill} />
          ) : activeTab === 'paymentlinks' ? (
            <PaymentLinks />
          ) : activeTab === 'backendpayments' ? (
            <BackendPayments />
          ) : activeTab === 'damagedproducts' ? (
            <DamagedProducts />
          ) : activeTab === 'refunds' ? (
            <Refunds />
          ) : activeTab === 'ndr' ? (
            <Ndr calls={calls} />
          ) : (
            <AllFieldsTable calls={calls} />
          )}
        </main>
      </div>

      {showChangePassword && (
        <ChangePasswordModal mode="self" onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

export default App;
